import React, {useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import {alpha, useTheme} from "@mui/material/styles";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import MoreHorizOutlinedIcon from "@mui/icons-material/MoreHorizOutlined";
import FeedOutlinedIcon from "@mui/icons-material/FeedOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";
import MovieFilterOutlinedIcon from "@mui/icons-material/MovieFilterOutlined";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import WavingHandOutlinedIcon from "@mui/icons-material/WavingHandOutlined";
import PlayCircleOutlineOutlinedIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import MuxPlayer from "@mux/mux-player-react";
import {NewLogoIconWithText} from "./HomeIcons";

export type InitialOnboardingStep =
    | "role"
    | "first_thing"
    | "mobile_demo"
    | "mobile_handoff";

interface InitialOnboardingModalProps {
    open: boolean;
    isMobile: boolean;
    isReturningMobileUser?: boolean;
    step: InitialOnboardingStep | null;
    roleValue?: string;
    firstThingValue?: string;
    demoPlaybackId?: string;
    onSelectRole: (value: string) => Promise<void> | void;
    onSelectFirstThing: (value: string) => Promise<void> | void;
    onContinueFromMobileDemo: () => void;
    onEmailMeLink: () => void;
    onCopyLink?: () => void;
    userEmail?: string;
    onLogout?: () => Promise<void> | void;
}

interface OnboardingOption {
    id: string;
    title: string;
    description?: string;
    icon: React.ReactNode;
    tone?: "primary" | "secondary";
}

const roleOptions: OnboardingOption[] = [
    {
        id: "creator_self_employed",
        title: "Creator / Self-employed",
        icon: <AutoAwesomeOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "customer_success_support",
        title: "Customer Success & Support",
        icon: <SupportAgentOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "design_research",
        title: "Design & Research",
        icon: <DesignServicesOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "engineering_product",
        title: "Engineering & Product",
        icon: <ComputerOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "hr_people_recruiting_ld",
        title: "HR (People, Recruiting, L&D)",
        icon: <GroupsOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "marketing",
        title: "Marketing",
        icon: <CampaignOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "sales",
        title: "Sales",
        icon: <TrendingUpOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "other",
        title: "Other",
        icon: <MoreHorizOutlinedIcon sx={{fontSize: 28}}/>,
    },
];

const firstThingOptions: OnboardingOption[] = [
    {
        id: "descript",
        title: "Descript",
        icon: <TextFieldsOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "riverside_zoom",
        title: "Riverside or Zoom",
        icon: <VideocamOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "premiere_davinci_fcp",
        title: "Premiere, DaVinci, or Final Cut",
        icon: <MovieFilterOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "audacity_garageband",
        title: "Audacity or GarageBand",
        icon: <GraphicEqOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "new_to_this",
        title: "I\u2019m new to recording and editing",
        icon: <WavingHandOutlinedIcon sx={{fontSize: 28}}/>,
    },
    {
        id: "other",
        title: "Other",
        icon: <MoreHorizOutlinedIcon sx={{fontSize: 28}}/>,
    },
];
const mobileHandoffPreviewPlaybackId = "lM2LZte1m2MiBts1rrj0146Ray64zc4vavoI91wv8Pz4";

const InitialOnboardingModal = ({
    open,
    isMobile,
    isReturningMobileUser = false,
    step,
    roleValue = "",
    firstThingValue = "",
    demoPlaybackId = "",
    onSelectRole,
    onSelectFirstThing,
    onContinueFromMobileDemo,
    onEmailMeLink,
    onCopyLink,
    userEmail,
    onLogout,
}: InitialOnboardingModalProps) => {
    const theme = useTheme();
    const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
    const [linkCopied, setLinkCopied] = useState(false);

    const handleCopyLink = () => {
        const link = window.location.origin;
        navigator.clipboard.writeText(link).then(() => {
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        });
        onCopyLink?.();
    };
    const [pendingSelectionId, setPendingSelectionId] = useState("");
    const [selectionDelayTimer, setSelectionDelayTimer] = useState<number | null>(null);

    useEffect(() => {
        setPendingSelectionId("");
        if (selectionDelayTimer != null) {
            window.clearTimeout(selectionDelayTimer);
            setSelectionDelayTimer(null);
        }
    }, [step]);

    useEffect(() => () => {
        if (selectionDelayTimer != null) {
            window.clearTimeout(selectionDelayTimer);
        }
    }, [selectionDelayTimer]);

    const selectedFirstThingOption = useMemo(
        () => firstThingOptions.find((option) => option.id === firstThingValue) ?? null,
        [firstThingValue]
    );

    const cardBorderColor = alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.3 : 0.12);
    const selectedCardBorderColor = alpha(theme.palette.primary.main, 0.5);
    const selectedCardBackground = theme.palette.mode === "dark"
        ? alpha(theme.palette.primary.main, 0.16)
        : alpha(theme.palette.primary.main, 0.08);

    const scheduleOptionSelect = (optionId: string, handler: (value: string) => Promise<void> | void) => {
        if (pendingSelectionId) return;
        setPendingSelectionId(optionId);
        const timerId = window.setTimeout(() => {
            void Promise.resolve(handler(optionId));
        }, 140);
        setSelectionDelayTimer(timerId);
    };

    const renderChoiceCard = ({
        option,
        selectedValue,
        onSelect,
        compact = false,
    }: {
        option: OnboardingOption;
        selectedValue: string;
        onSelect: (value: string) => Promise<void> | void;
        compact?: boolean;
    }) => {
        const isSelected = option.id === pendingSelectionId || option.id === selectedValue;

        // Compact = grid cards (role step), non-compact = list cards (first_thing step)
        if (compact) {
            return (
                <ButtonBase
                    key={option.id}
                    onClick={() => scheduleOptionSelect(option.id, onSelect)}
                    disabled={Boolean(pendingSelectionId)}
                    sx={{
                        width: "100%",
                        borderRadius: "8px",
                        border: `1px solid ${isSelected ? selectedCardBorderColor : alpha(theme.palette.common.black, 0.1)}`,
                        backgroundColor: isSelected ? selectedCardBackground : "#fff",
                        padding: {xs: "20px 12px", sm: "22px 16px"},
                        textAlign: "center",
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                        transition: "border-color 140ms ease, background-color 140ms ease",
                        minHeight: {xs: "100px", sm: "110px"},
                        "&:hover": pendingSelectionId
                            ? undefined
                            : {
                                borderColor: alpha(theme.palette.common.black, 0.25),
                            },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%",
                        }}
                    >
                        <Box sx={{color: alpha(theme.palette.common.black, 0.45)}}>
                            {option.icon}
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: "Inter",
                                fontSize: {xs: "12.5px", sm: "13px"},
                                fontWeight: 500,
                                lineHeight: {xs: "16px", sm: "18px"},
                                color: alpha(theme.palette.common.black, 0.75),
                            }}
                        >
                            {option.title}
                        </Typography>
                    </Box>
                </ButtonBase>
            );
        }

        // List-style card for first_thing step (single column, centered icon + text)
        return (
            <ButtonBase
                key={option.id}
                onClick={() => scheduleOptionSelect(option.id, onSelect)}
                disabled={Boolean(pendingSelectionId)}
                sx={{
                    width: "100%",
                    borderRadius: "8px",
                    border: `1px solid ${isSelected ? selectedCardBorderColor : alpha(theme.palette.common.black, 0.1)}`,
                    backgroundColor: isSelected ? selectedCardBackground : "#fff",
                    padding: {xs: "22px 16px", sm: "26px 20px"},
                    textAlign: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    display: "flex",
                    transition: "border-color 140ms ease, background-color 140ms ease",
                    "&:hover": pendingSelectionId
                        ? undefined
                        : {
                            borderColor: alpha(theme.palette.common.black, 0.25),
                        },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                    }}
                >
                    <Box sx={{color: alpha(theme.palette.common.black, 0.45)}}>
                        {option.icon}
                    </Box>
                    <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: "3px"}}>
                        <Typography
                            sx={{
                                fontFamily: "Inter",
                                fontSize: {xs: "14px", sm: "15px"},
                                fontWeight: 600,
                                lineHeight: {xs: "18px", sm: "20px"},
                                color: alpha(theme.palette.common.black, 0.8),
                            }}
                        >
                            {option.title}
                        </Typography>
                        {option.description ? (
                            <Typography
                                sx={{
                                    fontFamily: "Inter",
                                    fontSize: {xs: "12px", sm: "13px"},
                                    fontWeight: 400,
                                    lineHeight: {xs: "16px", sm: "18px"},
                                    color: alpha(theme.palette.common.black, 0.38),
                                }}
                            >
                                {option.description}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
            </ButtonBase>
        );
    };

    const renderRoleStep = () => (
        <Box
            sx={{
                width: "100%",
                maxWidth: isMobile ? "100%" : "680px",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                gap: {xs: "20px", sm: "24px"},
            }}
        >
            <Box sx={{display: "flex", flexDirection: "column", gap: "6px", textAlign: "center"}}>
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: {xs: "24px", sm: "26px"},
                        fontWeight: 500,
                        lineHeight: {xs: "30px", sm: "34px"},
                        letterSpacing: "-0.02em",
                        color: alpha(theme.palette.common.black, 0.85),
                    }}
                >
                    What best describes you?
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: {xs: "14px", sm: "15px"},
                        fontWeight: 400,
                        lineHeight: {xs: "20px", sm: "22px"},
                        color: alpha(theme.palette.common.black, 0.4),
                    }}
                >
                    Select one
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
                    gap: {xs: "8px", sm: "10px"},
                }}
            >
                {roleOptions.map((option) =>
                    renderChoiceCard({
                        option,
                        selectedValue: roleValue,
                        onSelect: onSelectRole,
                        compact: true,
                    })
                )}
            </Box>
        </Box>
    );

    const renderFirstThingStep = () => {
        return (
            <Box
                sx={{
                    width: "100%",
                    maxWidth: isMobile ? "100%" : "820px",
                    margin: "0 auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: {xs: "24px", sm: "28px"},
                }}
            >
                <Box sx={{display: "flex", flexDirection: "column", gap: "6px", textAlign: "center"}}>
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: {xs: "22px", sm: "26px"},
                            fontWeight: 500,
                            lineHeight: {xs: "28px", sm: "34px"},
                            letterSpacing: "-0.02em",
                            color: alpha(theme.palette.common.black, 0.85),
                        }}
                    >
                        What do you currently use to record or edit?
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "Inter",
                            fontSize: {xs: "13px", sm: "14px"},
                            fontWeight: 400,
                            lineHeight: {xs: "18px", sm: "20px"},
                            color: alpha(theme.palette.common.black, 0.38),
                            maxWidth: "520px",
                            margin: "0 auto",
                        }}
                    >
                        Choose the setup that's closest to how you work today. We'll use it to tailor tips and suggestions.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
                        gap: {xs: "8px", sm: "10px"},
                    }}
                >
                    {firstThingOptions.map((option) =>
                        renderChoiceCard({
                            option,
                            selectedValue: firstThingValue,
                            onSelect: onSelectFirstThing,
                            compact: true,
                        })
                    )}
                </Box>
            </Box>
        );
    };

    const renderMobileDemoStep = () => (
        <Box
            sx={{
                width: "100%",
                maxWidth: "820px",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                gap: {xs: "18px", sm: "22px"},
            }}
        >
            <Box sx={{display: "flex", flexDirection: "column", gap: "10px"}}>
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: {xs: "30px", sm: "40px"},
                        fontWeight: 700,
                        lineHeight: {xs: "36px", sm: "46px"},
                        letterSpacing: "-0.04em",
                        color: "var(--app-text-primary)",
                    }}
                >
                    Here&apos;s a quick look at {selectedFirstThingOption?.title || "Libretto"}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: {xs: "15px", sm: "18px"},
                        fontWeight: 400,
                        lineHeight: {xs: "22px", sm: "26px"},
                        color: "var(--app-text-muted)",
                    }}
                >
                    {selectedFirstThingOption?.description || "A quick preview before you continue on desktop."}
                </Typography>
            </Box>

            <Box
                sx={{
                    borderRadius: "24px",
                    overflow: "hidden",
                    border: `1px solid ${cardBorderColor}`,
                    backgroundColor: "#000",
                    minHeight: {xs: "220px", sm: "320px"},
                    boxShadow: "0px 28px 72px rgba(15, 23, 42, 0.12)",
                }}
            >
                {demoPlaybackId ? (
                    <MuxPlayer
                        playbackId={demoPlaybackId}
                        accentColor={theme.palette.primary.main}
                        style={{width: "100%", height: "100%", display: "block"}}
                        metadata={{
                            videoTitle: selectedFirstThingOption?.title || "Libretto onboarding demo",
                            ViewerUserId: "initial-onboarding",
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            minHeight: {xs: "220px", sm: "320px"},
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            gap: "12px",
                            color: "#F8FAFC",
                            padding: "24px",
                            textAlign: "center",
                        }}
                    >
                        <PlayCircleOutlineOutlinedIcon sx={{fontSize: 42}}/>
                        <Typography sx={{fontFamily: "Inter", fontSize: "16px", fontWeight: 600, color: "#F8FAFC"}}>
                            Demo video coming soon
                        </Typography>
                    </Box>
                )}
            </Box>

            <Button
                variant="contained"
                disableElevation
                onClick={onContinueFromMobileDemo}
                sx={{
                    textTransform: "none",
                    borderRadius: "14px",
                    fontFamily: "Inter",
                    fontWeight: 600,
                    fontSize: "16px",
                    lineHeight: "22px",
                    minHeight: "52px",
                    backgroundColor: "#111827",
                    color: "#F9FAFB",
                    "&:hover": {
                        backgroundColor: "#0B0B0B",
                    },
                }}
            >
                Continue
            </Button>
        </Box>
    );

    const mobileHandoffVideos = [
        {
            title: "Split speakers from one audio track",
            subtitle: "Separate speakers into individual tracks",
            youtubeId: "xCWEzkurVPw",
        },
        {
            title: "One click noise removal and speech leveling",
            subtitle: "Remove noise and level speech on Libretto",
            youtubeId: "sPCsBFHLcOY",
        },
        {
            title: "Creating scenes and adding B-roll",
            subtitle: "Add B-roll and arrange scenes on Libretto",
            youtubeId: "Q7DkeDjh870",
        },
    ];

    const renderMobileHandoffStep = () => (
        <Box
            sx={{
                width: "100%",
                maxWidth: "720px",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: "20px",
                paddingTop: 0,
                paddingBottom: "40px",
            }}
        >
            {/* Logo + Logout header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    mb: "4px",
                    "& > .logo-wrap svg": {
                        width: {xs: "128px", sm: "148px"},
                        height: "auto",
                    },
                }}
            >
                <Box className="logo-wrap" sx={{display: "flex", alignItems: "center"}}>
                    <NewLogoIconWithText/>
                </Box>
                <Button
                    variant="text"
                    disableRipple
                    onClick={onLogout}
                    sx={{
                        backgroundColor: "transparent",
                        color: "black",
                        height: "36px",
                        borderRadius: "999px",
                        padding: "8px 14px",
                        textTransform: "uppercase",
                        fontFamily: '"Space Mono", monospace',
                        fontWeight: 400,
                        fontSize: "15px",
                        lineHeight: "22px",
                        letterSpacing: "-1px",
                        whiteSpace: "nowrap",
                        border: "1px solid transparent",
                        "&:hover": {
                            backgroundColor: "transparent",
                            borderColor: "rgba(0,0,0,0.18)",
                            opacity: 0.75,
                        },
                    }}
                >
                    Log Out
                </Button>
            </Box>

            {/* Hero */}
            <Typography
                sx={{
                    fontFamily: "'Georgia', 'Times New Roman', serif",
                    fontSize: {xs: "36px", sm: "52px"},
                    fontWeight: 700,
                    lineHeight: {xs: "42px", sm: "58px"},
                    letterSpacing: "-0.02em",
                    color: alpha(theme.palette.common.black, 0.9),
                }}
            >
                Use Libretto on desktop
            </Typography>
            <Typography
                sx={{
                    fontFamily: "Inter",
                    fontSize: {xs: "15px", sm: "18px"},
                    fontWeight: 400,
                    lineHeight: {xs: "22px", sm: "26px"},
                    color: alpha(theme.palette.common.black, 0.5),
                    maxWidth: "420px",
                }}
            >
                Libretto works best on desktop. It takes minutes to clean up your first recording.
            </Typography>

            {/* Primary CTA */}
            <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", width: "100%"}}>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={onEmailMeLink}
                    sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: "16px",
                        lineHeight: "22px",
                        minHeight: "52px",
                        width: "100%",
                        maxWidth: "340px",
                        backgroundColor: theme.palette.primary.main,
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                        },
                    }}
                >
                    {userEmail ? `Send link to ${userEmail}` : "Send me the desktop link"}
                </Button>
                {/*<Typography*/}
                {/*    sx={{*/}
                {/*        fontFamily: "Inter",*/}
                {/*        fontSize: "12px",*/}
                {/*        lineHeight: "16px",*/}
                {/*        color: alpha(theme.palette.common.black, 0.35),*/}
                {/*    }}*/}
                {/*>*/}
                {/*    We&apos;ll email you a link to continue instantly*/}
                {/*</Typography>*/}
            </Box>

            {/* Secondary CTA */}
            <Button
                variant="text"
                disableElevation
                onClick={handleCopyLink}
                startIcon={<ContentCopyOutlinedIcon sx={{fontSize: "16px !important"}}/>}
                sx={{
                    textTransform: "none",
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "14px",
                    color: alpha(theme.palette.common.black, 0.5),
                    "&:hover": {
                        backgroundColor: "transparent",
                        color: alpha(theme.palette.common.black, 0.7),
                    },
                }}
            >
                {linkCopied ? "Link copied!" : "Copy link instead"}
            </Button>

            {/* Reassurance */}
            <Typography
                sx={{
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 500,
                    lineHeight: "18px",
                    color: alpha(theme.palette.common.black, 0.32),
                    letterSpacing: "-0.01em",
                }}
            >
                No download needed. Works in your browser.
            </Typography>

            {/* Divider */}
            <Box sx={{width: "100%", height: "1px", backgroundColor: alpha(theme.palette.common.black, 0.08), my: "4px"}}/>

            {/* Video showcase section */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: alpha(theme.palette.common.black, 0.7),
                        textAlign: "left",
                    }}
                >
                    See how it works
                </Typography>

                {mobileHandoffVideos.map((video) => (
                    <Box
                        key={video.youtubeId}
                        sx={{
                            width: "100%",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
                            backgroundColor: "#fff",
                        }}
                    >
                        {activeVideoId === video.youtubeId ? (
                            /* Inline YouTube embed */
                            <Box
                                sx={{
                                    width: "100%",
                                    aspectRatio: "16 / 9",
                                }}
                            >
                                <Box
                                    component="iframe"
                                    src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${video.youtubeId}&modestbranding=1&rel=0&showinfo=0`}
                                    allow="autoplay; encrypted-media"
                                    allowFullScreen
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                    }}
                                />
                            </Box>
                        ) : (
                            /* Card with thumbnail + text */
                            <ButtonBase
                                onClick={() => setActiveVideoId(video.youtubeId)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "14px",
                                    padding: "12px 14px",
                                    width: "100%",
                                    textAlign: "left",
                                }}
                            >
                                {/* Thumbnail */}
                                <Box
                                    sx={{
                                        width: "100px",
                                        minWidth: "100px",
                                        height: "56px",
                                        borderRadius: "8px",
                                        overflow: "hidden",
                                        backgroundColor: "#f0f0f0",
                                        position: "relative",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
                                        alt={video.title}
                                        sx={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "rgba(0,0,0,0.2)",
                                        }}
                                    >
                                        <PlayCircleOutlineOutlinedIcon
                                            sx={{fontSize: 26, color: "#fff", opacity: 0.9}}
                                        />
                                    </Box>
                                </Box>
                                {/* Title + Subtitle */}
                                <Box sx={{flex: 1}}>
                                    <Typography
                                        sx={{
                                            fontFamily: "Inter",
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            lineHeight: "18px",
                                            letterSpacing: "-0.01em",
                                            color: alpha(theme.palette.common.black, 0.8),
                                        }}
                                    >
                                        {video.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "Inter",
                                            fontSize: "12px",
                                            fontWeight: 400,
                                            lineHeight: "16px",
                                            color: alpha(theme.palette.common.black, 0.4),
                                            mt: "3px",
                                        }}
                                    >
                                        {video.subtitle}
                                    </Typography>
                                </Box>
                            </ButtonBase>
                        )}
                    </Box>
                ))}

                {/* Full walkthrough link */}
                <Box sx={{textAlign: "center", mt: "8px"}}>
                    <Box
                        component="a"
                        href="https://www.youtube.com/@libretto"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            fontFamily: "Inter",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: alpha(theme.palette.common.black, 0.4),
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            "&:hover": {
                                color: alpha(theme.palette.common.black, 0.6),
                            },
                        }}
                    >
                        Want the full walkthrough? Watch on YouTube
                        <OpenInNewOutlinedIcon sx={{fontSize: 13}}/>
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    const renderReturningMobileHandoffStep = () => (
        <Box
            sx={{
                width: "100%",
                maxWidth: "720px",
                margin: "0 auto",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: {xs: "28px", sm: "32px"},
                paddingTop: 0,
                paddingBottom: "40px",
                minHeight: "100%",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    mb: {xs: "12px", sm: "20px"},
                    "& > .logo-wrap svg": {
                        width: {xs: "128px", sm: "148px"},
                        height: "auto",
                    },
                }}
            >
                <Box className="logo-wrap" sx={{display: "flex", alignItems: "center"}}>
                    <NewLogoIconWithText/>
                </Box>
                <Button
                    variant="text"
                    disableRipple
                    onClick={onLogout}
                    sx={{
                        backgroundColor: "transparent",
                        color: "black",
                        height: "36px",
                        borderRadius: "999px",
                        padding: "8px 14px",
                        textTransform: "uppercase",
                        fontFamily: '"Space Mono", monospace',
                        fontWeight: 400,
                        fontSize: "15px",
                        lineHeight: "22px",
                        letterSpacing: "-1px",
                        whiteSpace: "nowrap",
                        border: "1px solid transparent",
                        "&:hover": {
                            backgroundColor: "transparent",
                            borderColor: "rgba(0,0,0,0.18)",
                            opacity: 0.75,
                        },
                    }}
                >
                    Log Out
                </Button>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: {xs: "18px", sm: "22px"},
                    width: "100%",
                    paddingTop: {xs: "20px", sm: "32px"},
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "'Georgia', 'Times New Roman', serif",
                        fontSize: {xs: "36px", sm: "52px"},
                        fontWeight: 700,
                        lineHeight: {xs: "42px", sm: "58px"},
                        letterSpacing: "-0.02em",
                        color: alpha(theme.palette.common.black, 0.9),
                        maxWidth: "640px",
                    }}
                >
                    Use Libretto on desktop
                </Typography>
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: {xs: "15px", sm: "18px"},
                        fontWeight: 400,
                        lineHeight: {xs: "22px", sm: "26px"},
                        color: alpha(theme.palette.common.black, 0.5),
                        maxWidth: "440px",
                    }}
                >
                    Libretto doesn&apos;t work on mobile. Use your computer to log into your account on the web.
                </Typography>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={onEmailMeLink}
                    sx={{
                        textTransform: "none",
                        borderRadius: "10px",
                        fontFamily: "Inter",
                        fontWeight: 600,
                        fontSize: "15px",
                        lineHeight: "22px",
                        letterSpacing: "-0.01em",
                        minHeight: "56px",
                        width: "100%",
                        maxWidth: "340px",
                        backgroundColor: "#111827",
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: "#0B0B0B",
                        },
                    }}
                >
                    Email me the link
                </Button>
            </Box>
        </Box>
    );

    const renderStep = () => {
        switch (step) {
            case "role":
                return renderRoleStep();
            case "first_thing":
                return renderFirstThingStep();
            case "mobile_demo":
                return renderMobileDemoStep();
            case "mobile_handoff":
                return isReturningMobileUser ? renderReturningMobileHandoffStep() : renderMobileHandoffStep();
            default:
                return null;
        }
    };

    return (
        <Dialog
            open={open}
            fullScreen
            onClose={undefined}
            BackdropProps={{
                sx: {
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    backgroundColor: alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.64 : 0.44),
                },
            }}
            PaperProps={{
                sx: {
                    margin: 0,
                    maxWidth: "100vw",
                    maxHeight: "100vh",
                    borderRadius: 0,
                    backgroundColor: "#f7f7f7",
                    boxShadow: "none",
                },
            }}
        >
            <DialogContent
                sx={{
                    padding: isMobile ? "28px 20px 26px" : "0 40px",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100vh",
                    justifyContent: isMobile ? "flex-start" : "center",
                    alignItems: "center",
                    overflowY: "auto",
                    paddingTop: isMobile ? (step === "mobile_handoff" ? "16px" : "40px") : "0",
                }}
            >
                {renderStep()}
            </DialogContent>
        </Dialog>
    );
};

export default InitialOnboardingModal;
